import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { MOCK_BLOG_POSTS } from '../data/mockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    try {
      const res = await fetch(`${API}/blog`, { signal: controller.signal });
      clearTimeout(timeoutId);
      
      if (res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setPosts(data);
            return;
          }
        }
      }
      console.warn('API returned invalid response for blog posts. Using fallback data.');
      setPosts(MOCK_BLOG_POSTS);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Failed to fetch posts, using fallback:', error);
      setPosts(MOCK_BLOG_POSTS);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(search.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080B14' }}>
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20" style={{ background: '#080B14' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="blog-title">
            Resources & Insights
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg mb-8">
            Stay updated with the latest trends in IT infrastructure, security best practices, 
            and industry insights from our expert team.
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search articles..."
              className="pl-12 bg-[#080B14]/40 border-blue-500/15 text-white placeholder:text-gray-500 focus:border-blue-500/50"
              data-testid="blog-search"
            />
          </div>
        </motion.div>

        {/* Posts Grid */}
        {filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-400">No articles found matching your search.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, idx) => (
              <motion.article
                key={post.post_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="group h-full"
                data-testid={`blog-post-${idx}`}
              >
                <Link to={`/blog/${post.slug}`} className="h-full block">
                  <div 
                    className="h-full flex flex-col rounded-2xl overflow-hidden transition-all duration-300 group-hover:border-blue-500/30 group-hover:shadow-[0_8px_30px_rgb(0,0,0,0.5)]"
                    style={{ background: '#0D1224', border: '1px solid rgba(99, 130, 255, 0.12)' }}
                  >
                    {/* Image */}
                    <div className="relative overflow-hidden aspect-video">
                      <img 
                        src={post.image_url || 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&q=80'}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0D1224] to-transparent opacity-60" />
                      <span className="absolute bottom-4 left-4 px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-xs font-medium border border-blue-500/30 backdrop-blur-sm">
                        {post.category}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(post.created_at)}</span>
                      </div>
                      <h2 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors line-clamp-2">
                        {post.title}
                      </h2>
                      <p className="text-gray-400 text-sm line-clamp-3 mb-4 leading-relaxed">
                        {post.excerpt}
                      </p>
                      <span className="mt-auto inline-flex items-center gap-2 text-blue-400 text-sm font-medium group-hover:gap-3 transition-all">
                        Read More <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
